import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from './common/Layout';
import SharingScope from './common/SharingScope';
import { axiosInstance } from '../services/api';
import { CATEGORIES, ILLNESS_SYMPTOMS, CATEGORY_TO_STORY_TYPE } from '../constants/logCategories';
import { getUser } from '../utils/secureStorage';
import WhosePersonSelector from './common/WhosePersonSelector';

// ── Fonts ──
const FONT_HEADING = "'Lora', Georgia, serif";
const FONT_BODY = "'Source Sans 3', sans-serif";

// ── Helper: build auto-title ──
function buildTitle(category, selectedPill, customPill, who, structuredData) {
  const cat = CATEGORIES.find(c => c.key === category);
  const pillLabel = selectedPill === 'other'
    ? (customPill || 'Other')
    : (cat?.pills.find(p => p.value === selectedPill)?.label || '');

  if (category === 'vehicle') {
    const parts = [structuredData.year, structuredData.make, structuredData.model].filter(Boolean);
    const car = parts.join(' ');
    return car ? `${car} \u2014 ${who || 'Unknown'}` : `Vehicle \u2014 ${who || 'Unknown'}`;
  }
  if (pillLabel) {
    return `${pillLabel} \u2014 ${who || 'Unknown'}`;
  }
  return `${cat?.label || 'Log'} \u2014 ${who || 'Unknown'}`;
}

// ── Reusable styled input ──
const inputStyle = (color) => ({
  width: '100%',
  minHeight: 50,
  padding: '12px 16px',
  fontSize: 16,
  fontFamily: FONT_BODY,
  border: '2px solid #D4CFC4',
  borderRadius: 12,
  outline: 'none',
  background: '#fff',
  color: '#333',
  transition: 'border-color 0.2s, box-shadow 0.2s',
  boxSizing: 'border-box',
});

const selectStyle = (color) => ({
  ...inputStyle(color),
  cursor: 'pointer',
  appearance: 'none',
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23999' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 14px center',
  paddingRight: 36,
});

const labelStyle = {
  display: 'block',
  fontSize: 14,
  fontWeight: 700,
  color: '#555',
  marginBottom: 6,
  fontFamily: FONT_BODY,
};

// ── Main Component ──
const QuickLog = () => {
  const navigate = useNavigate();
  const { id: logId } = useParams();
  const isEditMode = Boolean(logId);

  // Family members
  const [familyMembers, setFamilyMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(true);

  // Edit mode loading
  const [loadingLog, setLoadingLog] = useState(isEditMode);
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  // State
  const [activeCategory, setActiveCategory] = useState('illness');
  const [selectedPill, setSelectedPill] = useState('');
  const [customPill, setCustomPill] = useState('');
  const [selectedPersonId, setSelectedPersonId] = useState('');
  const [who, setWho] = useState('');
  const [showCustomWho, setShowCustomWho] = useState(false);
  const [dateText, setDateText] = useState('');
  const [dateStart, setDateStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');
  const [location, setLocation] = useState('');
  const [note, setNote] = useState('');
  const [structuredData, setStructuredData] = useState({});
  const [saving, setSaving] = useState(false);
  const [sharingScope, setSharingScope] = useState('single_family');
  const [sharingFamilyIds, setSharingFamilyIds] = useState([]);

  // Person selector state (whose log is this?)
  const [tier, setTier] = useState('mine');
  const [selectedChildIds, setSelectedChildIds] = useState([]);

  // Fetch family members on mount
  const calculateAge = useCallback((birthDate) => {
    if (!birthDate) return null;
    try {
      const birth = new Date(birthDate);
      const today = new Date();
      let age = today.getFullYear() - birth.getFullYear();
      const monthDiff = today.getMonth() - birth.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
        age--;
      }
      return age;
    } catch { return null; }
  }, []);

  useEffect(() => {
    const fetchFamilyMembers = async () => {
      try {
        setLoadingMembers(true);

        // Get family ID — same pattern as FamilyHub.js
        let familyId = localStorage.getItem('familyId');
        if (!familyId) {
          const userData = getUser() || {};
          familyId = userData.family?.id;
        }
        // Fallback: fetch from /families/ API
        if (!familyId) {
          const familiesRes = await axiosInstance.get('/families/');
          const families = familiesRes.data.results || familiesRes.data || [];
          if (families.length > 0) {
            familyId = families[0].id;
            localStorage.setItem('familyId', familyId);
          }
        }

        if (familyId) {
          const response = await axiosInstance.get(`/families/${familyId}/members/`);
          const membersData = response.data.members || response.data.results || response.data || [];
          const transformed = membersData.map(member => {
            const age = member.date_of_birth ? calculateAge(member.date_of_birth) : null;
            const isMinor = member.is_minor !== undefined ? member.is_minor : (age !== null && age < 18);
            const name = `${member.first_name || ''} ${member.last_name || ''}`.trim() || 'Unknown';
            return {
              id: member.id,
              name,
              relationship: member.role || '',
              age,
              isMinor,
            };
          });
          setFamilyMembers(transformed);
        }
      } catch (err) {
        console.error('Failed to load family members:', err);
      } finally {
        setLoadingMembers(false);
      }
    };
    fetchFamilyMembers();
  }, [calculateAge]);

  // Fetch existing log for edit mode
  useEffect(() => {
    if (!isEditMode) {
      setInitialLoadDone(true);
      return;
    }
    const fetchLog = async () => {
      try {
        setLoadingLog(true);
        const res = await axiosInstance.get(`/logs/${logId}/`);
        const log = res.data;
        setActiveCategory(log.category || 'illness');
        setSelectedPill(log.sub_type || '');
        setCustomPill(log.sub_type_custom || '');
        setSelectedPersonId(log.subject || '');
        setWho(log.subject_name || '');
        setShowCustomWho(log.subject ? false : Boolean(log.subject_name));
        setDateText(log.date_text || '');
        setDateStart(log.date_start || '');
        setDateEnd(log.date_end || '');
        setLocation(log.location || '');
        setNote(log.note || '');
        setStructuredData(log.structured_data || {});
      } catch (err) {
        console.error('Failed to load log:', err);
        alert('Failed to load log entry.');
        navigate('/dashboard');
      } finally {
        setLoadingLog(false);
        setInitialLoadDone(true);
      }
    };
    fetchLog();
  }, [isEditMode, logId, navigate]);

  // Current category config
  const catConfig = CATEGORIES.find(c => c.key === activeCategory);
  const catColor = catConfig?.color || '#556B2F';
  const catLight = catConfig?.colorLight || '#f5f5f5';
  const catBorder = catConfig?.colorBorder || '#ddd';

  // Clear form on category switch (skip during initial edit-mode load)
  useEffect(() => {
    if (!initialLoadDone) return;
    setSelectedPill('');
    setCustomPill('');
    setSelectedPersonId('');
    setWho('');
    setShowCustomWho(false);
    setDateText('');
    setDateStart('');
    setDateEnd('');
    setLocation('');
    setNote('');
    setStructuredData({});
  }, [activeCategory]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update a structured data field
  const updateSD = (key, value) => {
    setStructuredData(prev => ({ ...prev, [key]: value }));
  };

  // Focus ring handler
  const handleFocus = (e) => {
    e.target.style.borderColor = catColor;
    e.target.style.boxShadow = `0 0 0 3px ${catColor}22`;
  };
  const handleBlur = (e) => {
    e.target.style.borderColor = '#D4CFC4';
    e.target.style.boxShadow = 'none';
  };

  // Save handler
  const handleSave = async () => {
    if (!who.trim()) return;

    setSaving(true);
    const payload = {
      subject: selectedPersonId || null,
      subject_name: who.trim(),
      category: activeCategory,
      sub_type: selectedPill,
      sub_type_custom: customPill,
      date_text: dateText,
      date_start: dateStart || null,
      date_end: dateEnd || null,
      location: location.trim(),
      note: note.trim(),
      structured_data: structuredData,
      is_private: true,
      sharing_scope: sharingScope,
      family_ids: sharingFamilyIds,
      person_id: tier === 'child' && selectedChildIds.length > 0 ? selectedChildIds[0] : undefined,
    };
    try {
      if (isEditMode) {
        await axiosInstance.patch(`/logs/${logId}/`, payload);
        navigate(`/logs/${logId}`);
      } else {
        await axiosInstance.post('/logs/', payload);
        navigate('/dashboard');
      }
    } catch (err) {
      console.error('Failed to save log entry:', err);
      alert('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Navigate to Tell a Story with pre-populated data
  const handlePromote = () => {
    const title = buildTitle(activeCategory, selectedPill, customPill, who, structuredData);
    const storyType = CATEGORY_TO_STORY_TYPE[activeCategory] || 'family_memory';
    const params = new URLSearchParams({
      fromLog: 'true',
      storyType,
      title,
    });
    navigate(`/tell-a-story?${params.toString()}`);
  };

  // ── Render category-specific panel ──
  const renderPanel = () => {
    switch (activeCategory) {
      case 'condition': return renderConditionPanel();
      case 'illness': return renderIllnessPanel();
      case 'vehicle': return renderVehiclePanel();
      default: return null;
    }
  };

  // ── Pills component ──
  const renderPills = () => {
    if (!catConfig?.pills.length) return null;
    return (
      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle}>What type?</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {catConfig.pills.map(pill => {
            const isActive = selectedPill === pill.value;
            return (
              <button
                key={pill.value}
                onClick={() => {
                  setSelectedPill(isActive ? '' : pill.value);
                  if (pill.value !== 'other') setCustomPill('');
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '10px 16px',
                  minHeight: 42,
                  borderRadius: 24,
                  border: `2px solid ${isActive ? catColor : '#D4CFC4'}`,
                  background: isActive ? catLight : '#fff',
                  color: isActive ? catColor : '#555',
                  fontWeight: isActive ? 700 : 500,
                  fontSize: 15,
                  fontFamily: FONT_BODY,
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                }}
              >
                <span style={{ fontSize: 16 }}>{pill.icon}</span>
                {pill.label}
              </button>
            );
          })}
        </div>
        {selectedPill === 'other' && (
          <input
            type="text"
            value={customPill}
            onChange={(e) => setCustomPill(e.target.value)}
            placeholder="Type your own..."
            onFocus={handleFocus}
            onBlur={handleBlur}
            style={{ ...inputStyle(catColor), marginTop: 10 }}
          />
        )}
      </div>
    );
  };

  // Handle person selection from dropdown
  const handlePersonSelect = (e) => {
    const value = e.target.value;
    if (value === '__other__') {
      setSelectedPersonId('');
      setWho('');
      setShowCustomWho(true);
    } else if (value === '') {
      setSelectedPersonId('');
      setWho('');
      setShowCustomWho(false);
    } else {
      setSelectedPersonId(value);
      const member = familyMembers.find(m => m.id === value);
      setWho(member ? member.name : '');
      setShowCustomWho(false);
    }
  };

  // Build display label for a family member
  const getMemberLabel = (member) => {
    let label = member.name;
    const parts = [];
    if (member.relationship) parts.push(member.relationship);
    if (member.isMinor) parts.push('Minor');
    if (parts.length > 0) label += ` (${parts.join(' \u00B7 ')})`;
    return label;
  };

  // ── Who field (dropdown) ──
  const renderWhoField = (labelText) => (
    <div style={{ marginBottom: 16 }}>
      <label style={labelStyle}>{labelText || 'Who is this about?'}</label>
      <select
        value={showCustomWho ? '__other__' : selectedPersonId}
        onChange={handlePersonSelect}
        onFocus={handleFocus}
        onBlur={handleBlur}
        style={selectStyle(catColor)}
      >
        <option value="">
          {loadingMembers ? 'Loading family members...' : 'Select a family member...'}
        </option>
        {familyMembers.map(member => (
          <option key={member.id} value={member.id}>
            {getMemberLabel(member)}
          </option>
        ))}
        <option value="__other__">Someone else...</option>
      </select>
      {showCustomWho && (
        <input
          type="text"
          value={who}
          onChange={(e) => setWho(e.target.value)}
          placeholder='Type a name, e.g., "Great-grandpa Mohan"'
          onFocus={handleFocus}
          onBlur={handleBlur}
          style={{ ...inputStyle(catColor), marginTop: 8 }}
        />
      )}
    </div>
  );

  // ── Note field (common) ──
  const renderNoteField = () => (
    <div style={{ marginBottom: 0 }}>
      <label style={labelStyle}>Quick note (optional)</label>
      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Anything else you want to remember..."
        onFocus={handleFocus}
        onBlur={handleBlur}
        rows={3}
        style={{
          ...inputStyle(catColor),
          minHeight: 80,
          resize: 'vertical',
        }}
      />
    </div>
  );

  // ── Three-button row (Yes/Unsure/No) ──
  const renderThreeButtons = (field, options) => (
    <div style={{ display: 'flex', gap: 8 }}>
      {options.map(opt => {
        const isActive = structuredData[field] === opt.value;
        return (
          <button
            key={opt.value}
            onClick={() => updateSD(field, isActive ? '' : opt.value)}
            style={{
              flex: 1,
              minHeight: 42,
              borderRadius: 10,
              border: `2px solid ${isActive ? catColor : '#D4CFC4'}`,
              background: isActive ? catLight : '#fff',
              color: isActive ? catColor : '#666',
              fontWeight: isActive ? 700 : 500,
              fontSize: 15,
              fontFamily: FONT_BODY,
              cursor: 'pointer',
              transition: 'all 0.15s',
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );

  // ── CONDITION PANEL ──
  const renderConditionPanel = () => (
    <div style={{ animation: 'qlFadeIn 0.25s ease' }}>
      {renderPills()}
      {renderWhoField()}
      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>When diagnosed?</label>
        <input
          type="text"
          value={dateText}
          onChange={(e) => setDateText(e.target.value)}
          placeholder={'e.g., "Around 1998", "Summer of \'92"'}
          onFocus={handleFocus}
          onBlur={handleBlur}
          style={inputStyle(catColor)}
        />
      </div>
      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>Status</label>
        <select
          value={structuredData.status || ''}
          onChange={(e) => updateSD('status', e.target.value)}
          onFocus={handleFocus}
          onBlur={handleBlur}
          style={selectStyle(catColor)}
        >
          <option value="">Select status...</option>
          <option value="ongoing">Ongoing / Managing</option>
          <option value="resolved">Resolved</option>
          <option value="passed_away">Passed away from this</option>
          <option value="not_sure">Not sure</option>
        </select>
      </div>
      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>Runs in the family?</label>
        {renderThreeButtons('family_pattern', [
          { value: 'yes', label: 'Yes' },
          { value: 'not_sure', label: 'Unsure' },
          { value: 'no', label: 'No' },
        ])}
      </div>
      {renderNoteField()}
    </div>
  );

  // ── ILLNESS PANEL ──
  const renderIllnessPanel = () => (
    <div style={{ animation: 'qlFadeIn 0.25s ease' }}>
      {renderPills()}
      {renderWhoField()}
      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>Severity</label>
        <select
          value={structuredData.severity || ''}
          onChange={(e) => updateSD('severity', e.target.value)}
          onFocus={handleFocus}
          onBlur={handleBlur}
          style={selectStyle(catColor)}
        >
          <option value="">Select severity...</option>
          <option value="mild">Mild &mdash; stayed home</option>
          <option value="moderate">Moderate &mdash; saw a doctor</option>
          <option value="severe">Severe &mdash; hospitalized</option>
          <option value="critical">Very severe &mdash; ICU</option>
        </select>
      </div>
      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>Symptoms</label>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
          {ILLNESS_SYMPTOMS.map(symptom => {
            const symptoms = structuredData.symptoms || [];
            const isChecked = symptoms.includes(symptom.value);
            return (
              <label
                key={symptom.value}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '8px 10px', borderRadius: 10,
                  border: `1.5px solid ${isChecked ? catColor : '#E8E4DA'}`,
                  background: isChecked ? catLight : '#fff',
                  cursor: 'pointer', transition: 'all 0.15s',
                  fontSize: 14, fontFamily: FONT_BODY,
                  color: isChecked ? catColor : '#555',
                  fontWeight: isChecked ? 600 : 400,
                  minHeight: 40,
                }}
              >
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={() => {
                    const current = structuredData.symptoms || [];
                    const updated = isChecked
                      ? current.filter(s => s !== symptom.value)
                      : [...current, symptom.value];
                    updateSD('symptoms', updated);
                  }}
                  style={{ display: 'none' }}
                />
                <span style={{
                  width: 20, height: 20, borderRadius: 5, flexShrink: 0,
                  border: `2px solid ${isChecked ? catColor : '#ccc'}`,
                  background: isChecked ? catColor : '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all 0.15s',
                }}>
                  {isChecked && (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </span>
                <span>{symptom.icon} {symptom.label}</span>
              </label>
            );
          })}
        </div>
        <input
          type="text"
          value={structuredData.custom_symptom || ''}
          onChange={(e) => updateSD('custom_symptom', e.target.value)}
          placeholder="Other symptoms..."
          onFocus={handleFocus}
          onBlur={handleBlur}
          style={{ ...inputStyle(catColor), marginTop: 8 }}
        />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
        <div>
          <label style={labelStyle}>Started</label>
          <input
            type="date"
            value={dateStart}
            onChange={(e) => setDateStart(e.target.value)}
            onFocus={handleFocus}
            onBlur={handleBlur}
            style={inputStyle(catColor)}
          />
        </div>
        <div>
          <label style={labelStyle}>Recovered</label>
          <input
            type="date"
            value={dateEnd}
            onChange={(e) => setDateEnd(e.target.value)}
            onFocus={handleFocus}
            onBlur={handleBlur}
            style={inputStyle(catColor)}
          />
        </div>
      </div>
      {renderNoteField()}
    </div>
  );

  // ── VEHICLE PANEL ──
  const renderVehiclePanel = () => (
    <div style={{ animation: 'qlFadeIn 0.25s ease' }}>
      {renderPills()}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10, marginBottom: 16 }}>
        <div>
          <label style={labelStyle}>Year</label>
          <input
            type="text"
            value={structuredData.year || ''}
            onChange={(e) => updateSD('year', e.target.value)}
            placeholder="1987"
            onFocus={handleFocus}
            onBlur={handleBlur}
            style={inputStyle(catColor)}
          />
        </div>
        <div>
          <label style={labelStyle}>Make</label>
          <input
            type="text"
            value={structuredData.make || ''}
            onChange={(e) => updateSD('make', e.target.value)}
            placeholder="Chevrolet"
            onFocus={handleFocus}
            onBlur={handleBlur}
            style={inputStyle(catColor)}
          />
        </div>
        <div>
          <label style={labelStyle}>Model</label>
          <input
            type="text"
            value={structuredData.model || ''}
            onChange={(e) => updateSD('model', e.target.value)}
            placeholder="Camaro"
            onFocus={handleFocus}
            onBlur={handleBlur}
            style={inputStyle(catColor)}
          />
        </div>
        <div>
          <label style={labelStyle}>Color</label>
          <input
            type="text"
            value={structuredData.color || ''}
            onChange={(e) => updateSD('color', e.target.value)}
            placeholder="Red"
            onFocus={handleFocus}
            onBlur={handleBlur}
            style={inputStyle(catColor)}
          />
        </div>
      </div>
      {renderWhoField('Whose?')}
      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>When?</label>
        <input
          type="text"
          value={dateText}
          onChange={(e) => setDateText(e.target.value)}
          placeholder='e.g., "Around 1987"'
          onFocus={handleFocus}
          onBlur={handleBlur}
          style={inputStyle(catColor)}
        />
      </div>
      {renderNoteField()}
    </div>
  );

  if (loadingLog) {
    return (
      <Layout>
        <div style={{
          background: '#FAF9F5', minHeight: '100vh',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>{'\u{1F4CB}'}</div>
            <p style={{ fontSize: 17, color: '#999', fontFamily: FONT_BODY }}>Loading log...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={{ background: '#FAF9F5', minHeight: '100vh' }}>
        {/* Page Header */}
        <div style={{
          background: `linear-gradient(135deg, #eef2e4 0%, #e8efc8 100%)`,
          borderBottom: '3px solid #556B2F',
          padding: '20px 28px',
        }}>
          <div style={{ maxWidth: 800, margin: '0 auto' }}>
            <button
              onClick={() => navigate(isEditMode ? `/logs/${logId}` : '/dashboard')}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                background: 'rgba(255,255,255,0.7)', border: '1.5px solid #D4CFC4',
                borderRadius: 10, padding: '9px 18px', cursor: 'pointer',
                fontSize: 15, fontWeight: 600, color: '#556B2F',
                fontFamily: FONT_BODY, marginBottom: 14,
                transition: 'background 0.15s',
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <path d="M19 12H5" /><path d="m12 19-7-7 7-7" />
              </svg>
              {isEditMode ? 'Back to Log' : 'Back to Hub'}
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{
                width: 52, height: 52, borderRadius: 16,
                background: '#556B2F',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 26, color: '#fff',
              }}>
                {'\u{1F4CB}'}
              </div>
              <div>
                <h1 style={{
                  fontSize: 28, fontWeight: 800, color: '#2C3E1A', margin: 0,
                  fontFamily: FONT_HEADING,
                }}>
                  {isEditMode ? 'Edit Log' : 'Quick Log'}
                </h1>
                <p style={{
                  fontSize: 15, color: '#666', margin: '2px 0 0',
                  fontFamily: FONT_BODY,
                }}>
                  {isEditMode ? 'Update this log entry' : 'Record a fact in under 60 seconds'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div style={{ maxWidth: 800, margin: '0 auto', padding: '24px 28px 120px' }}>
          {/* Whose log is this? */}
          <div style={{ marginBottom: 20 }}>
            <WhosePersonSelector
              tier={tier}
              onTierChange={(t) => {
                setTier(t);
                // When switching to child, auto-set the "who" field
                if (t === 'child' && selectedChildIds.length > 0) {
                  const firstChild = familyMembers.find(m => m.id === selectedChildIds[0]);
                  if (firstChild) setWho(firstChild.name);
                }
              }}
              selectedChildIds={selectedChildIds}
              onSelectedChildIdsChange={(ids) => {
                setSelectedChildIds(ids);
                // Sync the "who" field with the first selected child
                if (ids.length > 0) {
                  const child = familyMembers.find(m => m.id === ids[0]);
                  if (child) {
                    setWho(child.name);
                    setSelectedPersonId(ids[0]);
                    setShowCustomWho(false);
                  }
                }
              }}
              itemLabel="log"
            />
          </div>

          {/* Log Card */}
          <div style={{
            background: '#fff', borderRadius: 20,
            border: '1px solid #E8E4DA',
            boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
            overflow: 'hidden',
          }}>
            {/* Category Tabs */}
            <div style={{
              display: 'flex', overflowX: 'auto',
              borderBottom: '2px solid #E8E4DA',
              padding: '0',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
            }}>
              {CATEGORIES.map(cat => {
                const isActive = activeCategory === cat.key;
                return (
                  <button
                    key={cat.key}
                    onClick={() => setActiveCategory(cat.key)}
                    style={{
                      flex: '0 0 auto',
                      display: 'flex', flexDirection: 'column',
                      alignItems: 'center', gap: 4,
                      padding: '14px 18px 10px',
                      border: 'none',
                      borderBottom: `3px solid ${isActive ? cat.color : 'transparent'}`,
                      background: isActive ? '#fff' : 'transparent',
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                      minWidth: 80,
                    }}
                  >
                    <span style={{ fontSize: 22 }}>{cat.icon}</span>
                    <span style={{
                      fontSize: 13, fontWeight: isActive ? 700 : 500,
                      color: isActive ? cat.color : '#888',
                      fontFamily: FONT_BODY,
                      whiteSpace: 'nowrap',
                    }}>
                      {cat.label}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Panel Header */}
            <div style={{ padding: '24px 28px 0' }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 12,
                marginBottom: 20,
              }}>
                <div style={{
                  width: 44, height: 44, borderRadius: 12,
                  background: catLight,
                  border: `1.5px solid ${catBorder}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 24,
                }}>
                  {catConfig?.icon}
                </div>
                <div style={{ flex: 1 }}>
                  <h2 style={{
                    fontSize: 20, fontWeight: 700, color: '#2C3E1A', margin: 0,
                    fontFamily: FONT_HEADING,
                  }}>
                    {catConfig?.label}
                  </h2>
                </div>
                <span style={{
                  fontSize: 12, fontWeight: 700,
                  color: catColor, background: catLight,
                  padding: '4px 10px', borderRadius: 10,
                  fontFamily: FONT_BODY,
                }}>
                  ~30s
                </span>
              </div>
            </div>

            {/* Active Panel */}
            <div style={{ padding: '0 28px 28px' }}>
              {renderPanel()}
            </div>
          </div>
        </div>

        {/* Sharing Scope */}
        <div style={{ maxWidth: 800, margin: '0 auto', padding: '0 28px' }}>
          <SharingScope
            value={sharingScope}
            onChange={(scope, familyIds) => {
              setSharingScope(scope);
              setSharingFamilyIds(familyIds);
            }}
            className="mt-4"
          />
        </div>

        {/* Sticky Action Bar */}
        <div style={{
          position: 'fixed', bottom: 0, left: 0, right: 0,
          background: '#fff',
          borderTop: '2px solid #E8E4DA',
          padding: '14px 28px',
          zIndex: 50,
          boxShadow: '0 -4px 16px rgba(0,0,0,0.06)',
        }}>
          <div style={{
            maxWidth: 800, margin: '0 auto',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <button
              onClick={handlePromote}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: 15, color: '#556B2F', fontWeight: 600,
                fontFamily: FONT_BODY,
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '8px 0',
              }}
            >
              {'\u{1F4D6}'} Want to tell the story behind this?{' '}
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <path d="M5 12h14" /><path d="m12 5 7 7-7 7" />
              </svg>
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !who.trim()}
              style={{
                padding: '14px 32px',
                borderRadius: 14,
                border: 'none',
                background: saving || !who.trim() ? '#ccc' : '#556B2F',
                color: '#fff',
                cursor: saving || !who.trim() ? 'default' : 'pointer',
                fontSize: 17,
                fontWeight: 700,
                fontFamily: FONT_BODY,
                transition: 'background 0.15s',
                display: 'flex', alignItems: 'center', gap: 8,
              }}
            >
              {saving ? 'Saving...' : isEditMode ? 'Update Log' : 'Save Log Entry'}
            </button>
          </div>
        </div>

        {/* Animations */}
        <style>{`
          @keyframes qlFadeIn {
            from { opacity: 0; transform: translateY(6px); }
            to { opacity: 1; transform: translateY(0); }
          }
          /* Hide scrollbar on category tabs */
          div::-webkit-scrollbar { display: none; }
        `}</style>
      </div>
    </Layout>
  );
};

export default QuickLog;
